import type { Promotion } from '../schemas/promotion.js';

/**
 * Pure promotion resolver.
 *
 * Given an array of promotions (the operator's full list) and a checkout
 * context, returns the eligible promotions, the savings each contributes,
 * and the total discount to apply to the cart subtotal.
 *
 * Pure — no I/O, no Firebase types. The caller (bookTicket / recordPayment
 * Cloud Functions, or a client-side preview) loads promotions + builds the
 * context, then calls this and stamps the result onto the ticket.
 *
 * Sprint 56 (MVP) scope:
 *   Triggers supported:
 *     - `always`     — always eligible (modulo conditions + validity)
 *     - `schedule`   — dows + timeWindow + dateRange filters
 *   Triggers NOT YET supported (will fall through as ineligible):
 *     - `lifecycle`  (needs customer history — defer until customer index is consumed)
 *     - `audience`   (needs segment resolution — defer)
 *     - `manual`     (needs customer-typed code — Phase 2)
 *
 *   Conditions supported:
 *     - `minSpend`              — cart subtotal threshold
 *     - `vehicleTypeIdIn`       — restrict by booked vehicle type
 *     - `outletIdIn`            — restrict by outlet (for multi-outlet tenants)
 *
 *   Effects supported:
 *     - `pct_off` on `cart`     — straight percentage off subtotal
 *     - `fixed_off` on `cart`   — fixed sen amount off subtotal (capped at subtotal)
 *
 *   Stacking: default `bestOnly` for both vouchers and other promotions.
 *   In `bestOnly` mode we keep only the single promotion with the largest
 *   savings. `allow` stacks every eligible promotion. `block` only applies
 *   if no other promotion already in the result set.
 *
 *   Status gate: only promotions with `status === 'active'` participate.
 *
 *   Kill switch: when the caller passes `context.promotionsPaused === true`
 *   (the tenant-wide "Pause all promotions" master toggle), the resolver
 *   short-circuits and returns zero savings — no promotion applies regardless
 *   of its own status. This is enforced server-side (bookTicket reads the flag
 *   from the loyalty config), so a paused tenant charges full price.
 *
 *   Validity gate: `now` must fall in `[validity.from, validity.to]`.
 *
 * The resolver never throws; bad inputs (missing snapshot, weird types)
 * silently skip the offending promotion. A `skippedReasons` array surfaces
 * those for debugging without breaking the checkout.
 */

/**
 * The trigger kinds this resolver can actually evaluate.
 *
 * This is the SINGLE source of truth for "will this promotion ever fire", and
 * it exists because the answer used to live only in the shape of the switch
 * below — where the dashboard could not see it.
 *
 * The consequence was a promotion that lied. `/loyalty/promotions` offers an
 * "Activate" button on every paused or draft promotion, with no notion that
 * some trigger kinds are unimplemented. An operator could take the seeded
 * "first visit" promotion, click Activate, watch the row turn **Active** — and
 * that promotion would never discount a single wash, because `triggerMatches`
 * drops `lifecycle` in its default branch. Nothing on any screen said so. The
 * resolver's own comment describes the promotion as "silently skipped", which
 * is exactly right about the code and exactly wrong for the operator, who has
 * planned a campaign around it.
 *
 * Exporting the list rather than re-stating it in the dashboard is the point:
 * a hand-copied duplicate would go stale the day `lifecycle` lands, and the UI
 * would then warn about a trigger that had started working — the same class of
 * stale claim, pointed the other way.
 *
 * Adding a kind here without implementing its `case` is caught by a test.
 */
export const SUPPORTED_TRIGGER_KINDS = ['always', 'schedule'] as const;

export type SupportedTriggerKind = (typeof SUPPORTED_TRIGGER_KINDS)[number];

/** Can the resolver evaluate this trigger kind at all? */
export function isTriggerSupported(kind: Promotion['triggers'][number]['kind']): boolean {
  return (SUPPORTED_TRIGGER_KINDS as readonly string[]).includes(kind);
}

/**
 * Can this promotion ever apply, regardless of today's date or cart?
 *
 * A promotion needs at least ONE evaluable trigger. With none, it is inert no
 * matter what the operator does to its status.
 */
export function canPromotionEverFire(promotion: Promotion): boolean {
  return promotion.triggers.some((t) => isTriggerSupported(t.kind));
}

export interface ResolverContext {
  /** Now (ms epoch). Caller controls — tests pass a fixed value. */
  readonly nowMs: number;
  /** IANA timezone for evaluating schedule windows. Defaults to Asia/Kuala_Lumpur. */
  readonly timezone?: string;
  /** Outlet the booking is at — used to evaluate `outletIdIn` condition. */
  readonly outletId: string;
  /** Vehicle type the customer picked — used to evaluate `vehicleTypeIdIn`. */
  readonly vehicleTypeId: string;
  /** Cart subtotal in sen (package + add-ons), before any discount. */
  readonly cartSubtotalSen: number;
  /**
   * Tenant-wide kill switch. When `true`, EVERY promotion is skipped
   * (reason `promotions_paused`) regardless of its own `status` — the
   * operator has hit "Pause all promotions" on `/loyalty/promotions`,
   * which flips `tenants/{tid}/loyaltyProgram/config.promotionsPaused`.
   *
   * Server-authoritative: the pricing path (bookTicket / appendItemsToTicket)
   * reads the flag and passes it here, so a paused tenant genuinely charges
   * full price. Optional — omitting it (client preview, older callers) means
   * "not paused" and preserves the pre-kill-switch behaviour.
   */
  readonly promotionsPaused?: boolean;
}

export interface ResolvedPromotion {
  readonly promotionId: string;
  readonly promotionName: string;
  readonly savingsSen: number;
  readonly templateKey: string | null;
}

export interface ResolveResult {
  /** Promotions actually applied after stacking rules. */
  readonly applied: readonly ResolvedPromotion[];
  /** Sum of savings — what bookTicket subtracts from cartSubtotal. */
  readonly totalSavingsSen: number;
  /** Debug breadcrumbs for promotions skipped + why. */
  readonly skipped: readonly { promotionId: string; reason: string }[];
}

export function resolvePromotions(input: {
  readonly promotions: readonly Promotion[];
  readonly context: ResolverContext;
}): ResolveResult {
  const { promotions, context } = input;

  // Tenant-wide kill switch — short-circuit BEFORE any per-promotion work.
  // Every promotion is reported as skipped (`promotions_paused`) so a caller
  // debugging "why no discount?" sees the master switch, not a silent zero.
  if (context.promotionsPaused === true) {
    return {
      applied: [],
      totalSavingsSen: 0,
      skipped: promotions.map((p) => ({ promotionId: p.id, reason: 'promotions_paused' })),
    };
  }

  const candidates: ResolvedPromotion[] = [];
  const skipped: { promotionId: string; reason: string }[] = [];

  for (const p of promotions) {
    const reason = evaluatePromotion(p, context);
    if (reason !== null) {
      skipped.push({ promotionId: p.id, reason });
      continue;
    }
    const savings = computeSavings(p, context);
    if (savings <= 0) {
      skipped.push({ promotionId: p.id, reason: 'zero_savings' });
      continue;
    }
    candidates.push({
      promotionId: p.id,
      promotionName: p.name['en'] ?? p.id,
      savingsSen: savings,
      templateKey: p.templateKey ?? null,
    });
  }

  // Stacking. MVP: any candidate's stacking mode === 'bestOnly' → keep
  // only the single largest. `allow` everywhere → stack all. `block` →
  // keep first eligible and drop the rest (caller can re-order
  // promotions by priority in a later sprint).
  const allAllow = promotions.every((p) => p.stacking.withOtherPromotions === 'allow');
  const anyBlock = promotions.some((p) => p.stacking.withOtherPromotions === 'block');

  let applied: ResolvedPromotion[];
  if (allAllow) {
    applied = candidates;
  } else if (anyBlock) {
    applied = candidates.slice(0, 1);
  } else {
    // bestOnly — pick the biggest savings.
    applied =
      candidates.length === 0
        ? []
        : [candidates.reduce((a, b) => (b.savingsSen > a.savingsSen ? b : a))];
  }

  const totalSavingsSen = applied.reduce((acc, c) => acc + c.savingsSen, 0);

  return { applied, totalSavingsSen, skipped };
}

// ── per-promotion evaluation ─────────────────────────────────────────

/**
 * Returns null if the promotion is eligible; otherwise a skip reason.
 */
function evaluatePromotion(p: Promotion, ctx: ResolverContext): string | null {
  if (p.status !== 'active') return `status_${p.status}`;

  // Validity window.
  const validFromMs = tsMs(p.validity.from);
  const validToMs = tsMs(p.validity.to);
  if (validFromMs > 0 && ctx.nowMs < validFromMs) return 'before_validity_start';
  if (validToMs > 0 && ctx.nowMs > validToMs) return 'after_validity_end';

  // Triggers — must satisfy AT LEAST ONE.
  const anyTriggerMatches = p.triggers.some((t) => triggerMatches(t, ctx));
  if (!anyTriggerMatches) return 'no_trigger_matches';

  // Conditions — must satisfy ALL (those that are present).
  const c = p.conditions;
  if (typeof c.minSpend === 'number' && ctx.cartSubtotalSen < c.minSpend) {
    return 'below_min_spend';
  }
  if (
    c.vehicleTypeIdIn &&
    c.vehicleTypeIdIn.length > 0 &&
    !c.vehicleTypeIdIn.includes(ctx.vehicleTypeId)
  ) {
    return 'vehicle_type_not_in_set';
  }
  if (c.outletIdIn && c.outletIdIn.length > 0 && !c.outletIdIn.includes(ctx.outletId)) {
    return 'outlet_not_in_set';
  }

  return null;
}

const WEEKDAY_TO_DOW: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/**
 * Wall-clock day-of-week (0=Sun) and `HH:MM` for an instant, evaluated in the
 * given IANA timezone via `Intl` (timezone-correct + DST-aware). Pure — safe in
 * this environment-agnostic package (Node ships full ICU; browsers have Intl).
 */
function zonedDowAndTime(nowMs: number, timeZone: string): { dow: number; hhmm: string } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(nowMs));
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  const dow = WEEKDAY_TO_DOW[get('weekday')] ?? 0;
  const hhmm = `${get('hour').padStart(2, '0')}:${get('minute').padStart(2, '0')}`;
  return { dow, hhmm };
}

function triggerMatches(trigger: Promotion['triggers'][number], ctx: ResolverContext): boolean {
  // Gate on the EXPORTED list, so the fact the dashboard reads is the same fact
  // that decides here. Behaviour is unchanged — every kind this rejects already
  // fell through the switch's default to `false` — but it can no longer drift:
  // implementing `lifecycle` without adding it to the list would leave it dead
  // while the switch claimed to handle it, and a test pins the two together.
  if (!isTriggerSupported(trigger.kind)) return false;

  switch (trigger.kind) {
    case 'always':
      return true;
    case 'schedule': {
      // Evaluate dow + time-of-day in the trigger's configured timezone — NOT
      // the runtime's local tz. Cloud Functions run in UTC, so using
      // `Date.getHours()` (local) made a "2-5pm Kuala Lumpur" window fire at
      // 2-5pm UTC (= 10pm-1am KL). Resolve wall-clock parts via Intl for the
      // IANA zone. (Bug caught the first time CI actually ran — 2026-07-05.)
      const tz = trigger.timezone ?? ctx.timezone ?? 'Asia/Kuala_Lumpur';
      const { dow, hhmm } = zonedDowAndTime(ctx.nowMs, tz);
      // dows — 0=Sun, matching the schema.
      if (trigger.dows && trigger.dows.length > 0) {
        if (!trigger.dows.includes(dow)) return false;
      }
      if (trigger.timeWindow) {
        if (hhmm < trigger.timeWindow.from || hhmm > trigger.timeWindow.to) return false;
      }
      if (trigger.dateRange) {
        const fromMs = tsMs(trigger.dateRange.from);
        const toMs = tsMs(trigger.dateRange.to);
        if (fromMs > 0 && ctx.nowMs < fromMs) return false;
        if (toMs > 0 && ctx.nowMs > toMs) return false;
      }
      return true;
    }
    // MVP doesn't resolve lifecycle / audience / manual yet. Those triggers
    // fall through as "not matched" so the promotion is silently skipped
    // (with reason `no_trigger_matches`). When the customer-history feed
    // exists, lifecycle becomes resolvable; for now we model it explicitly
    // as unsupported rather than crash.
    default:
      return false;
  }
}

function computeSavings(p: Promotion, ctx: ResolverContext): number {
  const e = p.effect;
  switch (e.type) {
    case 'pct_off':
      // appliesTo for MVP is treated as 'cart' regardless — line-level
      // discounting needs richer cart info (we have only the total here).
      return Math.floor((ctx.cartSubtotalSen * e.value) / 100);
    case 'fixed_off':
      return Math.min(e.value, ctx.cartSubtotalSen);
    // Other effect types (free_service, replace_price, bonus_points,
    // free_add_on) need richer integration with the cart/loyalty paths.
    // Defer to a later sprint; they silently produce 0 savings here so
    // the promotion is skipped with `zero_savings`.
    default:
      return 0;
  }
}

function tsMs(ts: unknown): number {
  if (!ts || typeof ts !== 'object') return 0;
  const obj = ts as { toMillis?: () => number; seconds?: number; nanoseconds?: number };
  if (typeof obj.toMillis === 'function') return obj.toMillis();
  if (typeof obj.seconds === 'number') {
    return obj.seconds * 1000 + Math.floor((obj.nanoseconds ?? 0) / 1_000_000);
  }
  return 0;
}
